import { Controller } from 'stimulus'

const limitSizeMBs = 5 // 5 MBytes
const limitSizeBytes = limitSizeMBs * 1024 * 1024

/**
 * アイコン選択UI
 */
export default class extends Controller {
  static get targets() {
    return ['icon', 'file']
  }

  connect() {
    if (this.hasIconTarget) {
      this.defaultIcon = getComputedStyle(this.iconTarget).backgroundImage
    }
  }

  processFile(e) {
    const file = e.target.files[0]
    if (!file) {
      this.reset()
      return
    }

    const errors = []
    if (file.size > limitSizeBytes) {
      errors.push(`ファイルサイズの上限は ${limitSizeMBs} MB です。`)
    }
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      errors.push('選べるファイルは jpeg か png のみです。')
    }
    if (errors.length) {
      alert(errors.join('') + 'お手数ですが写真を選びなおしてください。')
      this.reset()
      return
    }

    Promise.all([
      this.readAsDataURL(file),
      this.readAsArrayBuffer(file).then(buffer => this.getOrientation(buffer))
    ]).then(([dataUrl, orientation]) => {
      this.showImage(dataUrl, orientation)
    })
  }

  check(e) {
    const checked = e.target.checked
    if (checked) {
      this.reset()
    } else {
      this.restore()
    }
  }

  readAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      if (!FileReader) {
        reject('FileReader API is not supported')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.readAsArrayBuffer(file)
    })
  }

  readAsDataURL(file) {
    return new Promise((resolve, reject) => {
      if (!FileReader) {
        reject('FileReader API is not supported')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.readAsDataURL(file)
    })
  }

  /**
   * 画像の回転を取得する
   * @returns {number} 回転を表す数字。正しく表示するために数字に応じて以下の処理をする:
   *   1: 何もしない
   *   2: 左右反転
   *   3: 180度回転
   *   4: 180度回転し、左右反転
   *   5: 時計回りに90度回転し、左右反転
   *   6: 時計回りに90度回転
   *   7: 時計回りに270度回転し、左右反転
   *   8: 時計回りに270度回転
   */
  getOrientation(buffer) {
    const dataView = new DataView(buffer)
    const hasExif = dataView.getUint16(2) === 0xffe1
    if (!hasExif) {
      return 1
    }
    const exifStart = 12
    const le = dataView.getUint8(exifStart) === 0x49 // Little Endian?
    const ifdStart = exifStart + dataView.getUint32(exifStart + 4, le)
    const count = dataView.getUint16(ifdStart, le)
    for (let i = 0; i < count; i += 1) {
      const offset = ifdStart + 2 + i * 12
      const tag = dataView.getUint16(offset)
      if (tag === 0x112 /* Orientation */) {
        return dataView.getUint16(offset + 8, le)
      }
    }
    return 1
  }

  showImage(url, orientation) {
    const transform = []
    if ([2, 4, 5, 7].indexOf(orientation) >= 0) transform.push('scaleX(-1)')
    if (orientation === 3 || orientation === 4) transform.push('rotate(180deg)')
    if (orientation === 5 || orientation === 6) transform.push('rotate(90deg)')
    if (orientation === 7 || orientation === 8) transform.push('rotate(270deg)')
    this.iconTarget.style.transform = transform.join(' ')
    this.iconTarget.style.backgroundImage = `url(${url})`
    this.element.classList.add('-selected')
  }

  reset() {
    this.fileTarget.value = null
    this.element.classList.remove('-selected')
  }

  restore() {
    this.iconTarget.style.backgroundImage = this.defaultIcon
    this.element.classList.add('-selected')
  }
}
